/* eslint-disable max-nested-callbacks */
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import { selectStreamGoRoom } from '../../../redux/slices/room';
import { IRoomStreaming } from '../../../redux/streamGoApi';
import { StreamingUrlDialog } from '../RoomStreaming/StreamingUrlDialog';
import { StreamingUrlList } from '../RoomStreaming/StreamingUrlList';
import { AddButton } from '../RoomStreaming/styled';

export const useStreamingUrlsSettings = () => {
  const [loading, setIsLoading] = useState(true);
  const room = useSelector(selectStreamGoRoom);

  const [streamings, setStreamings] = useState<IRoomStreaming[]>([]);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<IRoomStreaming>({
    Description: '',
    StreamingUrl: '',
    StreamingKey: '',
    Id: -1,
  });

  useEffect(() => {
    if (loading && room) {
      setStreamings(room.RoomStreamings);
      setIsLoading(false);
    }
  }, [loading, room, streamings.length]);

  const resetForm = useCallback(() => {
    setEditingIndex(null);
    setFormData({
      Description: '',
      StreamingUrl: '',
      StreamingKey: '',
    });
  }, []);

  const handleOpenDialog = useCallback(
    (index: number | null = null) =>
      () => {
        if (index !== null) {
          setEditingIndex(index);
          setFormData(streamings[index]);
        } else {
          resetForm();
        }
        setDialogOpen(true);
      },
    [streamings, resetForm],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setTimeout(resetForm, 100);
  }, [resetForm]);

  const handleDelete = useCallback(
    (index: number) => () => {
      setStreamings(prev => prev.filter((_, i) => i !== index));
    },
    [],
  );

  const handleEdit = useCallback(
    (index: number) => () => {
      handleOpenDialog(index)();
    },
    [handleOpenDialog],
  );

  const handleSave = useCallback(() => {
    if (!formData.Description || !formData.StreamingUrl) {
      return;
    }

    if (editingIndex !== null) {
      // Update existing
      setStreamings(prev =>
        prev.map((item, i) => (i === editingIndex ? formData : item)),
      );
    } else {
      // Add new
      setStreamings(prev => [...prev, formData]);
    }

    handleCloseDialog();
  }, [formData, editingIndex, handleCloseDialog]);

  const handleFieldChange = useCallback(
    (field: keyof IRoomStreaming, value: string) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const getModel = () => streamings;

  return {
    streamingUrlsBlocks: [
      {
        title: '',
        element: (
          <>
            <StreamingUrlList
              onDelete={handleDelete}
              onEdit={handleEdit}
              streamings={streamings}
            />
            <AddButton
              onClick={handleOpenDialog(null)}
              startIcon={<AddIcon />}
              variant="outlined"
            >
              Add Streaming URL
            </AddButton>

            <StreamingUrlDialog
              editingIndex={editingIndex}
              formData={formData}
              onCancel={handleCloseDialog}
              onFieldChange={handleFieldChange}
              onSave={handleSave}
              open={dialogOpen}
            />
          </>
        ),
      },
    ],
    getStreamingUrlsModel: getModel,
    streamingsCount: streamings.length,
  };
};
